import { useEffect, useState } from 'react'
import { apiFetch } from '../../api/client'
import type { PulseOpen } from './pulseDrawer.js'
import { docOpenMode, fileKind } from './pulseDocKind.js'
import { fetchPulseFileBlob } from './pulseApi'
import { usePulseDrawerAction } from './PulseDrawer.tsx'

function formatSize(n?: number | null) {
  if (!Number.isFinite(Number(n))) return ''
  const v = Number(n)
  if (v < 1024) return `${v} B`
  if (v < 1024 * 1024) return `${Math.round(v / 1024)} KB`
  return `${(v / 1024 / 1024).toFixed(1)} MB`
}

function metaPath(projectId: number, open: Extract<PulseOpen, { kind: 'document' }>) {
  return open.source === 'att'
    ? `/api/projects/${projectId}/pulse/attachments/${open.id}`
    : `/api/projects/${projectId}/hub/uploads/${open.id}`
}

function filePath(projectId: number, open: Extract<PulseOpen, { kind: 'document' }>) {
  return open.source === 'att'
    ? `/api/projects/${projectId}/pulse/attachments/${open.id}/download`
    : `/api/projects/${projectId}/hub/uploads/${open.id}/download`
}

export default function PulseDocPanel({
  projectId,
  open,
}: {
  projectId: number
  open: Extract<PulseOpen, { kind: 'document' }>
}) {
  const [state, setState] = useState<{
    name: string; mime: string | null; size: number | null; status: 'ok' | 'unavailable' | 'loading'; preview?: string
  }>({ name: 'Document', mime: null, size: null, status: 'loading' })

  usePulseDrawerAction(() => (
    <button
      type="button"
      className="btn b-dk btn-sm pulse-drawer-download"
      data-testid="pulse-doc-download-btn"
      onClick={async () => {
        const blob = await fetchPulseFileBlob(filePath(projectId, open))
        const href = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = href
        a.download = state.name
        a.click()
        URL.revokeObjectURL(href)
      }}
    >
      Download
    </button>
  ), [projectId, open.id, open.source, state.name], state.status === 'ok')

  useEffect(() => {
    let cancelled = false
    let objectUrl = ''
    async function load() {
      try {
        const packed = await apiFetch<{ data: Record<string, unknown> }>('GET', metaPath(projectId, open))
        const row = packed.data || packed
        const name = String(row.original_name || row.filename || 'Document')
        const mime = (row.mime_type as string | null) || null
        const size = Number(row.file_size)
        const available = row.available === false ? false : true
        const mode = docOpenMode({ name, mime, available })
        if (cancelled) return
        if (mode === 'unavailable') {
          setState({ name, mime, size, status: 'unavailable' })
          return
        }
        if (mode === 'preview') {
          const blob = await fetchPulseFileBlob(filePath(projectId, open))
          if (cancelled) return
          objectUrl = URL.createObjectURL(blob)
          setState({ name, mime, size, status: 'ok', preview: objectUrl })
          return
        }
        setState({ name, mime, size: Number.isFinite(size) ? size : null, status: 'ok' })
      } catch {
        if (!cancelled) setState((s) => ({ ...s, status: 'unavailable' }))
      }
    }
    load()
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [projectId, open.id, open.source])

  if (state.status === 'loading') return <p data-testid="pulse-doc-loading">Loading…</p>
  if (state.status === 'unavailable') {
    return <p data-testid="pulse-doc-unavailable">This file is unavailable.</p>
  }
  const kind = fileKind(state.name, state.mime)
  const mode = docOpenMode({ name: state.name, mime: state.mime })
  const preview = mode === 'preview' && state.preview
  return (
    <div data-testid={preview ? 'pulse-doc-preview' : 'pulse-doc-download'}>
      <p className="pulse-detail-title" data-testid="pulse-doc-meta">{state.name}</p>
      <p className="pulse-row-meta">{[formatSize(state.size), state.mime || kind].filter(Boolean).join(' · ')}</p>
      {preview ? (
        <div className="pulse-viewer">
          {kind === 'image'
            ? <img src={state.preview} alt={state.name} />
            : <iframe title={state.name} src={state.preview} />}
        </div>
      ) : null}
    </div>
  )
}
